import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import Icon from 'Components/Icon';
import cn from 'classnames';
import { handleKeyDown } from 'Utils/handleKeyDown';
import styles from './styles.scss';
import { gaPush } from 'Utils/ga_push';
import classnames from 'classnames/bind';
import Typography, { headlinePropTypes } from 'Components/Typography';
import { qualifyLinksForWcag } from 'Utils/link';
import { replacePlaceholders } from 'Utils';

const cx = classnames.bind(styles);

export default class ConsumptionData extends React.Component {
  id = `consumptionData-${uuidv4()}`;

  constructor(props) {
    super(props);
    this.state = { isClosed: false, outerToggleControlAvailable: !!this.props.outerToggleIsClosed };
  }

  track = () => {
    const { outerIsClosed } = this.props;
    const { isClosed, outerToggleControlAvailable } = this.state;

    const closed = outerToggleControlAvailable ? outerIsClosed : isClosed;

    gaPush({
      event: 'gaEvent',
      category: 'CTA',
      action: 'consumption click',
      label: closed ? 'open' : 'close',
    });
  };

  toggleIsClosed = () => {
    this.track();

    if (this.state.outerToggleControlAvailable) {
      this.props.outerToggleIsClosed();
    } else {
      this.setState({ isClosed: !this.state.isClosed });
    }
  };

  buttonOnClick = () => {
    this.props.onClick();
    this.toggleIsClosed();
  };

  render() {
    const {
      actionIcon,
      ariaHidden,
      ariaLive,
      colorType = 'light',
      compact,
      consumptionText,
      deeplinkId,
      headline,
      inside,
      outerIsClosed,
      outerToggleIsClosed,
      role,
      smallVersion = false,
      style,
    } = this.props;

    let { hideCloseButton } = this.props;
    let { isClosed, outerToggleControlAvailable } = this.state;

    let _isClosed = isClosed;
    if (!outerToggleIsClosed && !hideCloseButton) {
      hideCloseButton = true;
      _isClosed = false;
    }

    if (!consumptionText.length || !consumptionText.filter((text) => !!text).length) {
      return null;
    }

    const expanded = outerToggleControlAvailable ? !outerIsClosed : !_isClosed;
    const moreThanOneConsumptionText = consumptionText.length > 1;

    return (
      <Fragment>
        <div
          id={this.id}
          className={cn({
            [styles.closed]: !expanded,
            [styles.notClosed]: expanded,
          })}
        >
          {actionIcon && (
            <button
              className={cx(styles.actionbtn, {
                [styles.showActionBtnClose]: actionIcon === 'close',
                [styles.showActionBtnInfo]: actionIcon === 'info',
              })}
              onClick={this.buttonOnClick}
              aria-controls={this.id}
              aria-label={'Verbrauchsangaben des Fahrzeugs ein- und ausblenden'}
              aria-expanded={expanded}
            >
              {actionIcon === 'close' && (
                <Icon name="close" className={styles.actionbtnIcon} inline={false} />
              )}
              {actionIcon === 'info' && (
                <Icon name="info" className={cx(styles.actionbtnIcon)} inline={false} />
              )}
            </button>
          )}

          <div
            className={cn(styles.container, {
              [styles.light]: colorType === 'white' || colorType === 'light',
              [styles.medium]: colorType === 'grey' || colorType === 'medium',
              [styles.dark]: colorType === 'black' || colorType === 'dark',
              [styles.insideOffer]: inside === 'offer',
              [styles.insideTile]: inside === 'tile',
              [styles.compact]: compact || moreThanOneConsumptionText,
              [styles.containerAdditions]: smallVersion,
            })}
            id={deeplinkId}
            style={style}
            aria-hidden={[true, false].includes(ariaHidden) ? String(ariaHidden) : undefined}
          >
            {moreThanOneConsumptionText && (
              <Typography.Headline
                level="h2"
                forceUppercase={true}
                value="Verbrauchsangaben"
                className="sr-only"
                {...headline}
              />
            )}
            <div aria-live={ariaLive} role={role}>
              <ul className={styles.textWrapper}>
                {consumptionText.map((text, index) => {
                  text = qualifyLinksForWcag(replacePlaceholders(text));

                  return (
                    <li
                      className={styles.item}
                      key={index}
                      dangerouslySetInnerHTML={{
                        __html: smallVersion ? text.replace('Verbrauchswerte: ', '') : text,
                      }}
                    />
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
        {!hideCloseButton && (
          <div className={styles.closeWrapper} data-in-offer={inside === 'offer'}>
            <div
              className={cn(
                smallVersion ? styles.smallCircle : styles.bigCircle,
                styles.closeCircle
              )}
              onClick={this.toggleIsClosed}
              onKeyDown={(event) => {
                handleKeyDown(event, 'Enter', this.toggleIsClosed);
              }}
              tabIndex={1}
            >
              <div
                className={cn(
                  smallVersion ? styles.smallCloseArrow : styles.bigCloseArrow,
                  styles.closeArrow
                )}
                data-is-closed={!expanded}
              ></div>
            </div>
          </div>
        )}
      </Fragment>
    );
  }
}

ConsumptionData.propTypes = {
  actionIcon: PropTypes.oneOf(['info', 'close']),
  ariaHidden: PropTypes.bool,
  ariaLive: PropTypes.string,
  colorType: PropTypes.oneOf(['white', 'grey', 'light', 'medium', 'dark', 'black']),
  compact: PropTypes.bool,
  consumptionText: PropTypes.array,
  deeplinkId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  headline: PropTypes.shape(headlinePropTypes),
  hideCloseButton: PropTypes.bool,
  inside: PropTypes.oneOf(['tile', 'offer']),
  onClose: PropTypes.func,
  outerIsClosed: PropTypes.bool,
  outerToggleIsClosed: PropTypes.func,
  role: PropTypes.string,
  style: PropTypes.object,
};
